(function initBankAccountSettings(global) {
  'use strict';

  // Kontonummeret er en noekkel, ikke en tekst. Banken skriver det med punktum
  // eller mellomrom for aa gjoere det lesbart — 1506.94.21609 — mens importfiler,
  // betalinger og Plummes egen matching sammenligner sifrene. Lagres skilletegnene
  // med, matcher ikke kontoen sitt eget nummer fra kontoutskriften, og
  // bankavstemmingen finner ikke kontoen den skal avstemme mot. Derfor
  // normaliseres nummeret allerede i feltet, slik importen alltid har gjort.
  //
  // Bokstaver beholdes: en konto i utenlandsk valuta oppgis ofte som IBAN
  // (NO93 8601 1117 947), og der er landkoden en del av nummeret.
  const ACCOUNT_NUMBER_MAX_LENGTH = 34;
  const normalizeAccountNumber = (value) => String(value == null ? '' : value)
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, ACCOUNT_NUMBER_MAX_LENGTH);

  // NS 4102 legger bankinnskudd paa 1920-serien. Skattetrekk er unntaket: trekket
  // skal staa paa egen konto adskilt fra foretakets egne midler
  // (skattebetalingsloven § 5-12), og den kontoen hoerer hjemme paa 1950. Neste
  // ledige hovedbokskonto er derfor ikke «forrige pluss en» — den foelger typen.
  const LEDGER_SERIES = {
    tax_withholding: { first: 1950, last: 1959 },
    default: { first: 1920, last: 1949 },
  };
  const ledgerSeries = (kind) => LEDGER_SERIES[String(kind || '').trim()] || LEDGER_SERIES.default;
  const ledgerCodeOf = (account) => String(
    account?.ledger || account?.ledgerAccountCode || account?.accountCode || account?.code || ''
  ).trim();

  // En deaktivert konto teller fortsatt som opptatt. Den er skjult for nye
  // betalinger, men bilagene som ligger paa hovedbokskontoen staar der fortsatt —
  // og gjenbrukes koden, blandes to bankkontoers historikk paa samme konto.
  const usedLedgerCodes = (accounts, excludeId) => {
    const used = new Set();
    (Array.isArray(accounts) ? accounts : []).forEach((account) => {
      if (excludeId && account?.id === excludeId) return;
      const code = ledgerCodeOf(account);
      if (!code) return;
      used.add(code);
      // Importerte kontoer kan ligge paa en variant av standardkoden — Fiken
      // eksporterer driftskontoen som `1920:10001`. Da er 1920 like opptatt.
      const base = code.match(/^\d{4}/);
      if (base) used.add(base[0]);
    });
    return used;
  };

  const nextLedgerAccountCode = (accounts, options) => {
    const settings = options && typeof options === 'object' ? options : { kind: options };
    const series = ledgerSeries(settings.kind);
    const used = usedLedgerCodes(accounts, settings.excludeId);
    for (let code = series.first; code <= series.last; code += 1) {
      const candidate = String(code);
      if (!used.has(candidate)) return candidate;
    }
    // Serien er full. Da er det ikke Plumme som skal velge kontoen — feltet staar
    // tomt, og foretaket setter selv koden det vil bruke.
    return '';
  };

  // Kursene hentes fra Norges Bank. En valuta uten notering der kan ikke regnes
  // om til balansedagens kurs, og da har avstemmingen ingen kurs aa forklare
  // omregningsdifferansen med. Lista er derfor den samme som noteringene
  // backenden slaar opp, pluss NOK.
  const CURRENCIES = ['NOK', 'SEK', 'DKK', 'EUR', 'USD', 'GBP', 'CHF', 'ISK', 'PLN', 'CZK', 'JPY', 'CAD', 'AUD'];
  const normalizeCurrency = (value) => String(value == null ? '' : value)
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3);

  // International payment identifiers are stored without visual separators.
  // This makes comparisons stable while the invoice can still render the
  // identifiers exactly as payment references.
  const normalizeIban = (value) => String(value == null ? '' : value)
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 34);
  const normalizeBic = (value) => String(value == null ? '' : value)
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 11);

  const accountNumberKey = (value) => normalizeAccountNumber(value);
  const invoicePaymentDetails = (accounts, accountNumber) => {
    const wanted = accountNumberKey(accountNumber);
    const account = wanted ? (Array.isArray(accounts) ? accounts : []).find((entry) =>
      accountNumberKey(entry?.accountNumber) === wanted
    ) : undefined;
    const show = account?.showIbanBicOnInvoice === true;
    return {
      showIbanBicOnInvoice: show,
      iban: show ? normalizeIban(account?.iban) : '',
      bic: show ? normalizeBic(account?.bic) : '',
    };
  };

  const invoicePaymentSnapshotFields = (accounts, accountNumber, existing) => {
    const wanted = accountNumberKey(accountNumber);
    const hasConfiguredAccount = (Array.isArray(accounts) ? accounts : []).some((entry) =>
      accountNumberKey(entry?.accountNumber) === wanted
    );
    const details = hasConfiguredAccount
      ? invoicePaymentDetails(accounts, accountNumber)
      : {
          showIbanBicOnInvoice: existing?.showIbanBicOnInvoice === true,
          iban: normalizeIban(existing?.bankAccountIban),
          bic: normalizeBic(existing?.bankAccountBic),
        };
    return {
      showIbanBicOnInvoice: details.showIbanBicOnInvoice,
      bankAccountIban: details.showIbanBicOnInvoice ? (details.iban || undefined) : undefined,
      bankAccountBic: details.showIbanBicOnInvoice ? (details.bic || undefined) : undefined,
    };
  };

  // Et lagret valutavalg som ikke lenger staar paa lista skal ikke forsvinne
  // stille naar kontoen aapnes — da hadde et fritekstfelt fra foer blitt endret
  // uten at noen valgte det. Den beholdes som eget valg til brukeren bytter.
  const currencyOptions = (current) => {
    const value = normalizeCurrency(current) || 'NOK';
    return CURRENCIES.includes(value) ? CURRENCIES.slice() : [...CURRENCIES, value];
  };

  const validIsoDate = (value) => {
    const raw = String(value || '').slice(0, 10);
    const parsed = new Date(`${raw}T00:00:00Z`);
    return /^\d{4}-\d{2}-\d{2}$/.test(raw)
      && !Number.isNaN(parsed.getTime())
      && parsed.toISOString().slice(0, 10) === raw;
  };
  const hasExplicitOpeningBalance = (account) => {
    const raw = account?.startBalance;
    if (raw === '' || raw == null) return false;
    const parsed = Number(String(raw).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(parsed);
  };
  // Zero is valid, but an empty value is unknown and cannot document the
  // beginning of a bank account's reconciliation chain.
  const openingSetupIssue = (account) => {
    if (!account || account.active === false || String(account.kind || '').toLowerCase() === 'private') return '';
    if (!validIsoDate(account.startBalanceDate)) return 'missing-opening-date';
    if (!hasExplicitOpeningBalance(account)) return 'missing-opening-balance';
    return '';
  };

  // A saved bank snapshot belongs to the accounting year that starts the day
  // after the snapshot date. This remains true when the currently selected or
  // configured year has later moved on.
  const openingYear = (configuredYear, startBalanceDate) => {
    if (validIsoDate(startBalanceDate)) {
      const parsed = new Date(`${String(startBalanceDate).slice(0, 10)}T00:00:00Z`);
      parsed.setUTCDate(parsed.getUTCDate() + 1);
      return parsed.getUTCFullYear();
    }
    const fallback = Number(configuredYear);
    return Number.isInteger(fallback) && fallback >= 1900 && fallback <= 2200 ? fallback : null;
  };

  const setupDate = ({ year, startBalanceDate, transitionDate, reconciliationFrom }) => {
    if (validIsoDate(startBalanceDate)) {
      const date = new Date(`${startBalanceDate.slice(0, 10)}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() + 1);
      return date.toISOString().slice(0, 10);
    }
    if (validIsoDate(transitionDate)) return transitionDate.slice(0, 10);
    if (validIsoDate(reconciliationFrom)) return reconciliationFrom.slice(0, 10);
    const fallback = openingYear(year, '');
    return fallback ? `${fallback}-01-01` : '';
  };

  const setupYear = (selectedYear, configuredYear) => openingYear(selectedYear, '') || openingYear(configuredYear, '');
  const setupValidation = ({supported, scenario, year, transitionDate, amount, needsCounter, counterAccountCode}) => {
    if (!supported) return 'unsupported';
    if (scenario === 'new_account') return 'new_account';
    if (!validIsoDate(transitionDate)) return 'date';
    if (Number(transitionDate.slice(0, 4)) !== Number(year)) return 'year';
    if (!Number.isFinite(amount)) return 'amount';
    if (needsCounter && !counterAccountCode) return 'counter';
    return '';
  };

  global.PlummeBankAccountSettings = {
    setupWarnings: (account = {}, persistedAccount) => account.active === false || account.kind === 'private' ? {} : {
      name: !String(account.name || '').trim(),
      accountNumber: !normalizeAccountNumber(account.accountNumber) && !normalizeIban(account.iban),
      ledger: !ledgerCodeOf(account),
      opening: Boolean(openingSetupIssue(persistedAccount || account)),
      unsaved: !persistedAccount,
    },
    openingBankAccounts: (opening = {}, chart = [], country) => {
      if (String(country || '').toUpperCase() !== 'NO') return [];
      const lines = opening.completedEntry?.lines?.length ? opening.completedEntry.lines : opening.balanceLines || [];
      const totals = new Map();
      lines.forEach(line => {
        const code = String(line.accountCode || line.code || '');
        const account = chart.find(row => String(row.code || row.accountNumber) === code);
        if (!/^19[2-5]\d/.test(String(account?.equivalentCode || code))) return;
        const current = totals.get(code) || { code, name: account?.name || code, amount: 0 };
        current.amount = Math.round((current.amount + Number(line.debit || 0) - Number(line.credit || 0)) * 100) / 100;
        totals.set(code, current);
      });
      return [...totals.values()].sort((a, b) => a.code.localeCompare(b.code));
    },
    setupYear, setupDate, setupValidation,
    ACCOUNT_NUMBER_MAX_LENGTH,
    CURRENCIES,
    normalizeAccountNumber,
    normalizeCurrency,
    normalizeIban,
    normalizeBic,
    invoicePaymentDetails,
    invoicePaymentSnapshotFields,
    currencyOptions,
    nextLedgerAccountCode,
    hasExplicitOpeningBalance,
    openingSetupIssue,
    openingYear,
  };
})(typeof window !== 'undefined' ? window : globalThis);
